<template>
  <!-- 术语列表 -->
  <div class="find-replace-dialog">
    <el-dialog
      top="7vh"
      width="400px"
      title="查找替换"
      :visible="visible"
      :close-on-click-modal="false"
      @close="closeDialog"
      v-dotclickoutside="closeDialog"
      :modal="false"
    >
      <h4>查找</h4>
      <el-input
        ref="findInput"
        v-model="findText"
        @keyup.enter.native="handleFindEnter()"
        @input="handleFindInput"
        placeholder="输入查找内容"
        autofocus
      >
        <div slot="suffix" class="counter">
          <i class="el-icon-arrow-left" @click="findPrev"></i>
          {{ findIndex }} / {{ findMatches.length }}
          <i class="el-icon-arrow-right" @click="findNext"></i>
        </div>
      </el-input>
      <h4>替换为</h4>
      <el-input v-model="replaceText" placeholder="输入替换内容"></el-input>
      <div class="operate-bar">
        <el-button
          type="default"
          :disabled="!findText"
          @click="handleFindAll()"
        >
          查找
        </el-button>
        <div>
          <el-button
            type="default"
            :disabled="!canReplace"
            @click="handleReplaceOne"
          >
            替换
          </el-button>
          <el-button
            type="default"
            :disabled="!canReplace"
            @click="handleReplaceAll"
          >
            全部替换
          </el-button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { qcUpdateTextBatch } from '@/api/dot/qc';
import { throttle } from 'lodash-es';

export default {
  name: 'FindReplaceDialog',
  props: ['visible', 'pRefs', 'staffId', 'qcTaskId', 'updateListFn'],
  data() {
    return {
      findText: '',
      replaceText: '',
      findIndex: 0,
      findMatches: [],
      lastFindText: '',
      willDie: false,
    };
  },
  watch: {
    visible(val) {
      // 弹窗每次出现都要拉取术语列表
      if (val) {
        this.focusFindInput();
        this.findText && this.handleFindAll(true);
      } else {
        this.handleClose();
      }
    },
  },
  computed: {
    // 含有richtext的行
    rtRowList() {
      const result = [];
      for (const key in this.pRefs) {
        if (this.pRefs.hasOwnProperty.call(this.pRefs, key)) {
          const element = this.pRefs[key];
          if (key.indexOf('richtext') !== -1 && element) {
            result.push({
              [key]: element,
              refKey: key,
            });
          }
        }
      }

      return result.filter((item) => {
        const rtInstance = this.getRtInstance(item[item.refKey]);
        return rtInstance && !rtInstance.readonly;
      });
    },
    canReplace() {
      return this.findMatches.length && this.findText !== this.replaceText;
    },
  },
  created() {
    window.addEventListener('keydown', this.handleKeyDown);
  },
  destroyed() {
    window.removeEventListener('keydown', this.handleKeyDown);
  },
  methods: {
    handleFindInput: throttle(function () {
      if (this.findText) {
        this.handleFindAll();
      } else {
        this.findMatches = [];
        this.findIndex = 0;
        this.clearMatches();
      }
    }, 300),
    handleFindAll(keepIndex) {
      if (!this.findText) return;
      this.clearMatches();
      // keepIndex: 处理单个替换后的自动查找和关闭后重开的查找
      if (keepIndex && this.findIndex > 0) {
        this.findIndex--;
      } else {
        this.findIndex = 0;
      }

      this.rtRowList.forEach((item) => {
        const rtColumn = item[item.refKey];
        item.matches = this.getRtInstance(rtColumn)?.findAll(this.findText);
      });
      // 重新组合上一步查到的匹配项
      this.findMatches = this.buildMatches();
      this.findNext();
      this.lastFindText = this.findText;
    },
    handleFindEnter() {
      if (!this.findText) return;
      if (this.lastFindText !== this.findText) {
        this.handleFindAll();
      } else {
        this.findNext();
      }
    },
    getRtInstance(rtColumn) {
      return rtColumn.$refs?.['richtext'];
    },
    findOne() {
      const match = this.findMatches[this.findIndex - 1];
      match.rtColumn?.$refs?.['richtext'].findOne(match.index);
    },
    handleReplaceAll() {
      if (!this.canReplace) return;
      let prompt = `确定将此文档内的 ${this.findMatches.length} 处 “${this.findText}” 全部替换为 “${this.replaceText}” 吗？`;
      if (!this.replaceText) {
        prompt = `确定将此文档内的 ${this.findMatches.length} 处 “${this.findText}” 全部清除吗？`;
      }
      this.$confirm(prompt, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        customClass: 'find-replace-confirm',
        type: 'warning',
      })
        .then(() => {
          this.doReplaceAll();
        })
        .catch(() => {});
    },
    doReplaceAll() {
      if (!this.canReplace) return;
      this.rtRowList.forEach((item) => {
        const rtColumn = item[item.refKey];
        this.getRtInstance(rtColumn)?.replaceAll(this.replaceText);
      });

      const textList = [];
      this.findMatches.forEach((match) => {
        const id = match.rtColumn.row?.id;
        // 去重
        if (!textList.find((item) => item.sentId === id)) {
          textList.push({
            sentId: match.rtColumn.row?.id,
            qcText: match.rtColumn.row?.editedPeText,
          });
        }
      });
      this.toUpdateData(textList, true);
      this.handleFindAll();
    },
    handleReplaceOne() {
      if (!this.canReplace) return;
      const match = this.findMatches[this.findIndex - 1];
      const rtColumn = match.rtColumn;
      this.getRtInstance(rtColumn)?.replaceOne(this.replaceText, match);

      const textList = [
        {
          sentId: rtColumn.row?.id,
          qcText: rtColumn.row?.editedPeText,
        },
      ];
      this.toUpdateData(textList).then(() => {
        // 修改成功后，状态改为已修改
        rtColumn.row.qcStatus = 2;
        rtColumn.row.peTextShow = rtColumn.row?.editedPeText;
        rtColumn.row.isDisabled = true;
      });
      this.handleFindAll(true);
    },
    toUpdateData(list, updateAll = false) {
      return qcUpdateTextBatch({
        operateQcTaskId: this.qcTaskId,
        operateQcId: this.staffId,
        textList: list,
      })
        .then(() => {
          this.$message.success('替换成功');
          updateAll && this.updateListFn();
        })
        .catch(() => {
          if (this.willDie) return;
          this.willDie = true;
          setTimeout(() => {
            this.reflash();
          }, 1500);
        });
    },
    buildMatches() {
      let result = [];
      this.rtRowList.forEach((item) => {
        result = [
          ...result,
          // 组合匹配到的项
          ...item.matches?.map((match) => ({
            ...match,
            rtColumn: item[item.refKey],
          })),
        ];
      });

      return result;
    },
    clearMatches() {
      this.rtRowList.forEach((item) => {
        const rtColumn = item[item.refKey];
        this.getRtInstance(rtColumn)?.clearMatches();
      });
    },
    findPrev() {
      if (!this.findMatches.length) return;
      if (this.findIndex === 1) {
        this.findIndex = this.findMatches.length;
      } else {
        this.findIndex--;
      }
      this.findOne();
    },
    findNext() {
      if (!this.findMatches.length) return;
      if (this.findIndex === this.findMatches.length) {
        this.findIndex = 1;
      } else {
        this.findIndex++;
      }
      this.findOne();
    },
    handleKeyDown(event) {
      // 查看上下文弹窗中不能查找搜索
      const peContextDrawerSign = document.querySelector(
        '.pe-context-drawer-sign',
      );
      if (peContextDrawerSign) return;

      if ((event.metaKey || event.ctrlKey) && event.key === 'f') {
        this.$emit('update:visible', true);
      }
      if (event.key === 'Escape') {
        this.$emit('update:visible', false);
      }
    },
    focusFindInput() {
      this.$nextTick(() => {
        if (this.$refs.findInput) {
          this.$refs.findInput.focus();
        }
      });
    },
    closeDialog(e) {
      if (document.querySelector('.find-replace-button')?.contains(e?.target))
        return;
      if (document.querySelector('.find-replace-confirm')?.contains(e?.target))
        return;

      this.$emit('update:visible', false);
    },
    handleClose() {
      this.clearMatches();
    },
    reflash() {
      this.$router.replace({
        path: '/refresh',
      });
    },
  },
};
</script>
<style lang="scss" scope>
.find-replace-dialog {
  .el-input {
    margin-top: 5px;
    margin-bottom: 15px;
    .el-input__suffix-inner {
      display: inline-block;
      height: 100%;

      .counter {
        display: flex;
        height: 100%;
        align-items: center;
        font-size: 14px;
        color: #303133;
        > i {
          font-size: 16px;
          cursor: pointer;
          margin: 5px;
        }
      }
    }
  }

  .operate-bar {
    margin-top: 10px;
    display: flex;
    justify-content: space-between;
  }

  .el-dialog__wrapper {
    left: 10%;
    padding: 50px;
    right: unset;
    bottom: unset;
    .el-dialog {
      box-shadow: 0px 5px 9px 0px #d5e3df, -5px 0px 9px 0px #d5e3df;
    }
  }
}
</style>
